import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { useEffect, useState } from 'react';
import classes from './addRowModalDrugReferenceByNDC.module.scss';
import { toast } from 'react-toastify';

interface FormData {
  fg_ndc: string;
  drug_name: string;
  bottle_stock_code: number;
  bottle_description: string;
  cap_stock_code: number;
  cap_description: string;
}

interface AddRowModalDrugReferenceFgNdcProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
  initialValues?: FormData;
  isEdit?: boolean;
}

const AddRowModalDrugReferenceFgNdc = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit
}: AddRowModalDrugReferenceFgNdcProps) => {
  const [formData, setFormData] = useState<FormData>({
    fg_ndc: '',
    drug_name: '',
    bottle_stock_code: 0,
    bottle_description: '',
    cap_stock_code: 0,
    cap_description: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const environment = import.meta.env.VITE_ENVIRONMENT;

  useEffect(() => {
    if (initialValues && visible) {
      setFormData({ ...initialValues });
      setErrors({});
    } else if (!isEdit && visible) {
      // Reset form for new entry
      setFormData({
        fg_ndc: '',
        drug_name: '',
        bottle_stock_code: 0,
        bottle_description: '',
        cap_stock_code: 0,
        cap_description: ''
      });
      setErrors({});
    }
  }, [initialValues, visible, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.fg_ndc) {
      newErrors.fg_ndc = 'NDC is required';
    }
    if (!formData.drug_name) {
      newErrors.drug_name = 'Drug Name is required';
    }
    if (!formData.bottle_stock_code && formData.bottle_stock_code !== 0) {
      newErrors.bottle_stock_code = 'Bottle Stock Code is required';
    }

    // Format validation
    const ndcFormat = /^\d{5}-\d{3}-\d{2}$/;
    if (formData.fg_ndc && !ndcFormat.test(formData.fg_ndc)) {
      newErrors.fg_ndc = 'NDC should be in format: #####-###-##';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        await onSubmit(formData);
        // Only close modal on success
        onHide();
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for Drug Reference by NDC data!');
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit Drug Reference' : 'Add New Drug Reference'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '600px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>NDC</label>
            <InputText
              value={formData.fg_ndc}
              onChange={e => handleInputChange('fg_ndc', e.target.value)}
              placeholder="e.g. 12345-678-90"
              className={`${classes.inputText} ${errors.fg_ndc ? 'p-invalid' : ''}`}
            />
            {errors.fg_ndc && <small className="p-error">{errors.fg_ndc}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>Drug Name</label>
            <InputText
              value={formData.drug_name}
              onChange={e => handleInputChange('drug_name', e.target.value)}
              placeholder="Enter Drug Name"
              className={`${classes.inputText} ${errors.drug_name ? 'p-invalid' : ''}`}
            />
            {errors.drug_name && <small className="p-error">{errors.drug_name}</small>}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Bottle Stock Code</label>
            <InputNumber
              value={formData.bottle_stock_code}
              onValueChange={e => handleInputChange('bottle_stock_code', e.value || 0)}
              placeholder="Enter Bottle Stock Code"
              className={`${classes.inputNumber} ${errors.bottle_stock_code ? 'p-invalid' : ''}`}
              useGrouping={false}
            />
            {errors.bottle_stock_code && (
              <small className="p-error">{errors.bottle_stock_code}</small>
            )}
          </div>

          <div className={classes.formGroup}>
            <label>Bottle Description</label>
            <InputText
              value={formData.bottle_description}
              onChange={e => handleInputChange('bottle_description', e.target.value)}
              placeholder="Enter Bottle Description"
              className={classes.inputText}
            />
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Cap Stock Code</label>
            <InputNumber
              value={formData.cap_stock_code}
              onValueChange={e => handleInputChange('cap_stock_code', e.value || 0)}
              placeholder="Enter Cap Stock Code"
              className={classes.inputNumber}
              useGrouping={false}
            />
          </div>

          <div className={classes.formGroup}>
            <label>Cap Description</label>
            <InputText
              value={formData.cap_description}
              onChange={e => handleInputChange('cap_description', e.target.value)}
              placeholder="Enter Cap Description"
              className={classes.inputText}
            />
          </div>
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button label="Cancel" icon="pi pi-times" onClick={onHide} severity="danger" text />

        <Button
          label="Save"
          icon="pi pi-check"
          onClick={handleSubmit}
          className={classes.saveButton}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalDrugReferenceFgNdc;
